import { ref, onMounted, onUnmounted } from 'vue';
import { useMessage } from 'naive-ui';

export interface VersionInfo {
  version: string;
  buildTime: string;
  buildNumber: number;
  note?: string;
  history?: VersionInfo[];
}

const CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
const FORCE_RELOAD_DELAY = 3000; // 3 seconds warning before reload
// Throttle for return-to-tab checks — focus + visibilitychange both fire on a
// single tab switch, and rapid tab switching must not spam version.json.
const RETURN_CHECK_THROTTLE = 10 * 1000;
// Wake-from-sleep detection (2026-09-15). A tab that stays in front while the
// computer sleeps gets no focus/visibility event on wake, so it used to wait up
// to 5 minutes for the next periodic check — long enough to save data from an
// out-of-date page. A cheap local timer notices the gap and checks at once.
const WAKE_HEARTBEAT_INTERVAL = 30 * 1000; // local clock compare only, no network
const WAKE_GAP_THRESHOLD = 90 * 1000; // heartbeat this late ⇒ the machine slept
// A check that fails (e.g. network still reconnecting after wake) retries
// quickly instead of waiting for the next 5-minute cycle.
const FAILED_CHECK_RETRY_DELAY = 15 * 1000;
const FAILED_CHECK_MAX_RETRIES = 4;

// Build time embedded at compile time by vite.config.ts `define`.
// Used to detect stale cached code on very first page load after a deploy.
declare const __APP_BUILD_TIME__: string;

/**
 * Automatic version detection and forced update system
 *
 * How it works:
 * 1. On app load, fetch and store current version from /version.json
 * 2. Every 5 minutes, check if version.json has changed
 *    (plus an immediate re-check when the user returns to the tab —
 *    on iPad PWA the interval freezes while suspended, so without this
 *    a reopened app could sit on a stale build for up to 5 minutes)
 * 3. If new version detected:
 *    - Clear all Service Workers
 *    - Clear all caches
 *    - Show countdown notification
 *    - Force hard reload (breaks cache)
 *
 * Benefits:
 * - Zero user intervention needed
 * - Works for all users (tech-savvy or not)
 * - Works for PWAs without uninstall
 * - Clears stale Service Worker caches automatically
 * - Force logout NOT needed - this runs on every page load
 */
export function useVersionCheck(options: { autoCheck?: boolean } = { autoCheck: true }) {
  const message = useMessage();
  const currentVersion = ref<VersionInfo | null>(null);
  const isUpdating = ref(false);
  let checkInterval: number | null = null;
  let lastReturnCheck = 0;
  let wakeHeartbeat: number | null = null;
  let lastHeartbeat = Date.now();
  let retryTimer: number | null = null;
  let failedChecks = 0;

  /**
   * Fetch version info with cache-busting
   */
  const fetchVersion = async (): Promise<VersionInfo | null> => {
    try {
      // Add timestamp to break cache
      const cacheBuster = `?t=${Date.now()}`;
      const response = await fetch(`/version.json${cacheBuster}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        console.warn('[VersionCheck] Failed to fetch version.json');
        return null;
      }

      const version: VersionInfo = await response.json();
      console.log('[VersionCheck] Fetched version:', version);
      return version;
    } catch (error) {
      console.error('[VersionCheck] Error fetching version:', error);
      return null;
    }
  };

  /**
   * Clear all Service Workers and caches, then hard reload
   */
  const performUpdate = async () => {
    if (isUpdating.value) return;
    isUpdating.value = true;

    console.log('[VersionCheck] 🚀 New version detected! Starting update process...');

    try {
      // 1. Activate any waiting SW (skipWaiting). Only unregister if none waiting —
      // unregister on a waiting SW aborts the install and traps users on stale code.
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        console.log(`[VersionCheck] Handling ${registrations.length} Service Worker(s)...`);
        for (const reg of registrations) {
          if (reg.waiting) {
            reg.waiting.postMessage({ type: 'SKIP_WAITING' });
          } else {
            await reg.unregister();
          }
        }
      }

      // 2. Clear all caches
      const cacheNames = await caches.keys();
      console.log(`[VersionCheck] Clearing ${cacheNames.length} cache(s)...`);
      await Promise.all(cacheNames.map((name) => caches.delete(name)));

      // 3. Show countdown notification
      message.warning('偵測到系統更新，將在 3 秒後自動重新載入...', {
        duration: 3000,
      });

      // 4. Force hard reload after delay
      setTimeout(() => {
        console.log('[VersionCheck] ♻️ Performing hard reload...');
        // window.location.reload(true) is deprecated, use these methods instead:
        window.location.href = window.location.href.split('?')[0] + '?_=' + Date.now();
      }, FORCE_RELOAD_DELAY);
    } catch (error) {
      console.error('[VersionCheck] Update failed:', error);
      message.error('自動更新失敗，請手動重新整理頁面');
      isUpdating.value = false;
    }
  };

  /**
   * Check if a new version is available
   */
  const checkForUpdates = async () => {
    if (isUpdating.value) return;

    const latestVersion = await fetchVersion();
    if (!latestVersion) {
      scheduleRetryAfterFailure();
      return;
    }
    failedChecks = 0;

    // First time loading - compare against the build-time timestamp baked into this JS bundle.
    // If the fetched version.json has a DIFFERENT buildTime, it means this cached bundle is stale
    // (e.g., iPad PWA serving old SW cache after a deploy). Trigger immediate update.
    if (!currentVersion.value) {
      currentVersion.value = latestVersion;
      console.log('[VersionCheck] Initial version stored:', latestVersion.version);

      // Compare against compile-time constant to detect stale cached code on first load
      const embeddedBuildTime =
        typeof __APP_BUILD_TIME__ !== 'undefined' ? __APP_BUILD_TIME__ : null;
      if (embeddedBuildTime && embeddedBuildTime !== latestVersion.buildTime) {
        console.log(
          '[VersionCheck] Stale cache detected on first load!',
          'Cached bundle built at:',
          embeddedBuildTime,
          'Server version built at:',
          latestVersion.buildTime,
        );
        await performUpdate();
      }
      return;
    }

    // Check if version changed
    const versionChanged =
      currentVersion.value.buildNumber !== latestVersion.buildNumber ||
      currentVersion.value.buildTime !== latestVersion.buildTime;

    if (versionChanged) {
      console.log('[VersionCheck] 🎉 New version available!');
      console.log('[VersionCheck] Old:', currentVersion.value);
      console.log('[VersionCheck] New:', latestVersion);

      // Trigger automatic update
      await performUpdate();
    } else {
      console.log('[VersionCheck] ✓ Version up to date:', currentVersion.value.version);
    }
  };

  /** One quick retry after a failed check (bounded; reset by any success). */
  function scheduleRetryAfterFailure() {
    if (retryTimer !== null || failedChecks >= FAILED_CHECK_MAX_RETRIES) return;
    failedChecks += 1;
    retryTimer = window.setTimeout(() => {
      retryTimer = null;
      void checkForUpdates();
    }, FAILED_CHECK_RETRY_DELAY);
  }

  /**
   * The computer slept (or the browser froze the tab): the heartbeat fires far
   * later than scheduled. Check immediately rather than waiting for the next
   * 5-minute cycle.
   */
  const handleHeartbeat = () => {
    const now = Date.now();
    const gap = now - lastHeartbeat;
    lastHeartbeat = now;
    if (gap > WAKE_GAP_THRESHOLD) {
      console.log(`[VersionCheck] Woke after ${Math.round(gap / 1000)}s, checking version...`);
      failedChecks = 0;
      void checkForUpdates();
    }
  };

  /** Network came back (typical right after wake) — check now. */
  const handleOnline = () => {
    console.log('[VersionCheck] Back online, checking version...');
    failedChecks = 0;
    void checkForUpdates();
  };

  /** Page restored from the browser's back/forward cache — its code may be old. */
  const handlePageShow = (event: PageTransitionEvent) => {
    if (event.persisted) void checkForUpdates();
  };

  /**
   * Re-check when the user returns to the tab. Throttled because `focus` and
   * `visibilitychange` both fire on a single tab switch; guarded on visibility
   * so a `focus` while hidden (e.g. window focus without tab visibility) is ignored.
   */
  const handleReturnToTab = () => {
    if (document.visibilityState !== 'visible') return;
    const now = Date.now();
    if (now - lastReturnCheck < RETURN_CHECK_THROTTLE) return;
    lastReturnCheck = now;
    console.log('[VersionCheck] Tab returned to foreground, checking version...');
    void checkForUpdates();
  };

  /**
   * Start periodic version checking
   */
  const startVersionCheck = () => {
    // Initial check on mount
    checkForUpdates();

    // Periodic checks every 5 minutes (idle backstop for long-focused tabs)
    checkInterval = window.setInterval(() => {
      console.log('[VersionCheck] Performing periodic version check...');
      checkForUpdates();
    }, CHECK_INTERVAL);

    // Immediate check on return-to-tab — covers suspended PWAs / background
    // tabs where the interval was frozen or throttled by the browser
    window.addEventListener('focus', handleReturnToTab);
    document.addEventListener('visibilitychange', handleReturnToTab);

    // Wake / reconnect / back-forward restore — see the constants above.
    lastHeartbeat = Date.now();
    wakeHeartbeat = window.setInterval(handleHeartbeat, WAKE_HEARTBEAT_INTERVAL);
    window.addEventListener('online', handleOnline);
    window.addEventListener('pageshow', handlePageShow);

    console.log(
      `[VersionCheck] Started periodic checks (every ${CHECK_INTERVAL / 1000 / 60} minutes) + return-to-tab checks`,
    );
  };

  /**
   * Stop periodic version checking
   */
  const stopVersionCheck = () => {
    if (checkInterval) {
      clearInterval(checkInterval);
      checkInterval = null;
      console.log('[VersionCheck] Stopped periodic checks');
    }
    window.removeEventListener('focus', handleReturnToTab);
    document.removeEventListener('visibilitychange', handleReturnToTab);
    if (wakeHeartbeat !== null) {
      clearInterval(wakeHeartbeat);
      wakeHeartbeat = null;
    }
    if (retryTimer !== null) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('pageshow', handlePageShow);
  };

  /**
   * Lifecycle hooks
   */
  onMounted(() => {
    // Skip auto-reload in development — version.json is mutated by production
    // builds while the dev server keeps serving the old __APP_BUILD_TIME__,
    // causing an infinite stale-cache → reload → stale-cache loop.
    if (import.meta.env.DEV) {
      fetchVersion().then((version) => {
        if (version) {
          currentVersion.value = version;
        }
      });
      return;
    }

    if (options.autoCheck) {
      startVersionCheck();
    } else {
      // If autoCheck is false, just fetch the version once for display purposes (without reload trigger)
      fetchVersion().then((version) => {
        if (version) {
          currentVersion.value = version;
        }
      });
    }
  });

  onUnmounted(() => {
    stopVersionCheck();
  });

  return {
    currentVersion,
    isUpdating,
    checkForUpdates,
  };
}
